// The public Interactive Brokers setup guide at /guides/interactive-brokers (and /es/...): each
// step of creating the Flex Query and token, with a simplified drawing of that IBKR screen and the
// thing to click glowing. The add-account wizard links here.
import { Link } from 'react-router-dom';
import { PublicShell, usePageLanguage, useTitle } from './PublicPage';
import { useI18n } from './i18n';
import { IBKR_GUIDE_PATH, IBKR_STEPS, IBKR_TROUBLE } from './ibkrGuideData';
import { TRIAL_DAYS } from './planData';

// A simplified screen: a window with a title bar, drawn in plain HTML.
function Screen({ title, children }) {
  return (
    <div className="ig-screen" aria-hidden="true">
      <div className="ig-bar"><i /><i /><i /><span>{title}</span></div>
      <div className="ig-body">{children}</div>
    </div>
  );
}
const Hot = ({ children, as: Tag = 'span' }) => <Tag className="ig-hot">{children}</Tag>;
const Field = ({ label, value, hot }) => (
  <div className="ig-field"><span>{label}</span>{hot ? <Hot as="b">{value}</Hot> : <b>{value}</b>}</div>
);
const Tick = ({ on }) => <span className={`ig-tick${on ? ' on' : ''}`}>{on ? '✓' : ''}</span>;

function Drawing({ screen }) {
  const { t } = useI18n();
  const portal = t('IBKR Client Portal (simplified)');
  const list = (hotInfo) => (
    <div className="ig-panel">
      <div className="ig-panel-head"><strong>Activity Flex Query</strong>{hotInfo ? <span className="ig-icon">+</span> : <Hot>+</Hot>}</div>
      {hotInfo && <div className="ig-row">{hotInfo === 'id' ? <Hot>ⓘ</Hot> : <span className="ig-icon">ⓘ</span>}<span>Market Matrix</span><span className="ig-faint">✎ ✕ ➜</span></div>}
    </div>
  );
  switch (screen) {
    case 'flex-list':
      return (
        <Screen title={portal}>
          <div className="ig-tabs"><span>Statements</span><b>Flex Queries</b><span>Other Reports</span></div>
          {list(null)}
        </Screen>
      );
    case 'query-name':
      return (
        <Screen title={portal}>
          <Field label="Query Name" value="Market Matrix" />
          <div className="ig-label">Sections</div>
          <div className="ig-list"><span>Account Information</span><span>Cash Transactions</span><span>Open Positions</span><Hot>Trades</Hot></div>
        </Screen>
      );
    case 'trades':
      return (
        <Screen title="Trades">
          <div className="ig-label">Options</div>
          <div className="ig-checks"><span><Tick /> Orders</span><Hot><Tick on /> Executions</Hot><span><Tick /> Closed Lots</span></div>
          <div className="ig-label">Fields</div>
          <div className="ig-row"><Hot>Select All</Hot><span className="ig-faint">Account ID · Symbol · Date/Time · Quantity · Price…</span></div>
          <div className="ig-actions"><Hot as="b">Save</Hot></div>
        </Screen>
      );
    case 'positions':
      return (
        <Screen title="Open Positions">
          <div className="ig-label">Options</div>
          <div className="ig-checks"><Hot><Tick on /> Summary</Hot><span><Tick /> Lot</span></div>
          <div className="ig-label">Fields</div>
          <div className="ig-row"><Hot>Select All</Hot><span className="ig-faint">Symbol · Position · Mark Price · Unrealized P/L…</span></div>
          <div className="ig-actions"><Hot as="b">Save</Hot></div>
        </Screen>
      );
    case 'delivery':
      return (
        <Screen title={portal}>
          <div className="ig-label">Delivery Configuration</div>
          <Field label="Format" value="XML" hot />
          <Field label="Period" value="Last 365 Calendar Days" hot />
          <div className="ig-label">General Configuration</div>
          <Field label="Date Format" value="yyyyMMdd" />
          <Field label="Time Format" value="HHmmss" />
          <Field label="Date/Time Separator" value="; (semi-colon)" />
          <div className="ig-actions"><Hot as="b">Continue</Hot></div>
        </Screen>
      );
    case 'create':
      return (
        <Screen title={portal}>
          <Field label="Query Name" value="Market Matrix" />
          <Field label="Sections" value="Trades" />
          <Field label="Format" value="XML" />
          <div className="ig-actions"><span className="ig-ghost">Back</span><Hot as="b">Create</Hot></div>
        </Screen>
      );
    case 'query-id':
      return (
        <Screen title={portal}>
          {list('id')}
          <div className="ig-pop"><span>Query ID</span><b>1234567</b></div>
        </Screen>
      );
    case 'token':
      return (
        <Screen title={portal}>
          <div className="ig-panel">
            <div className="ig-panel-head"><strong>Flex Web Service Configuration</strong><Hot>⚙</Hot></div>
            <Field label="Flex Web Service Status" value="Enabled" />
          </div>
          <div className="ig-actions"><Hot as="b">Generate New Token</Hot></div>
          <Field label={t('Token')} value="•••• •••• •••• ••••" />
        </Screen>
      );
    case 'connect':
      return (
        <Screen title="Market Matrix">
          <div className="ig-label">{t('Live sync with {platform}', { platform: 'Interactive Brokers' })}</div>
          <Field label={t('Flex Web Service token')} value="•••••••••••••" />
          <Field label={t('Query ID')} value="1234567" />
          <div className="ig-actions"><Hot as="b">{t('Connect')}</Hot></div>
        </Screen>
      );
    case 'accounts':
      return (
        <Screen title="Market Matrix">
          <div className="ig-checks col"><Hot><Tick on /> U1234567</Hot></div>
          <div className="ig-row"><span className="ig-faint">{t('Backfill history')}</span><span>7d · 30d · 90d · <b>{t('All')}</b></span></div>
          <div className="ig-actions"><Hot as="b">{t('Start tracking')} ▸</Hot></div>
        </Screen>
      );
    default:
      return null;
  }
}

export default function IbkrGuidePage({ user, forceLang }) {
  usePageLanguage(forceLang, IBKR_GUIDE_PATH);
  const { t } = useI18n();
  useTitle(t('How to sync Interactive Brokers trades | Market Matrix'));
  return (
    <PublicShell user={user} className="firm-page ibkr-guide">
      <header className="page-title firm-hero">
        <p className="eyebrow">{t('Guide · about 5 minutes')}</p>
        <h1>{t('Sync your Interactive Brokers trades automatically')}</h1>
        <p className="subtitle">{t('Set it up once: create a Flex Query and a token in IBKR, paste them into Market Matrix, and your trades come in every hour. No more file exports.')}</p>
      </header>

      <ol className="ig-steps">
        {IBKR_STEPS.map((s, i) => (
          <li key={s.title} className="ig-step">
            <div className="ig-text">
              <span className="ig-num">{i + 1}</span>
              <h2>{t(s.title)}</h2>
              <p>{t(s.text)}</p>
            </div>
            <Drawing screen={s.screen} />
          </li>
        ))}
      </ol>
      <p className="field-hint ig-note">{t('The drawings are simplified. IBKR’s screens may look a little different, but the names of the buttons are the same.')}</p>

      <section className="firm-section">
        <h2>{t('Something not working?')}</h2>
        <dl className="firm-faq">
          {IBKR_TROUBLE.map(([q, a]) => <div key={q}><dt>{t(q)}</dt><dd>{t(a)}</dd></div>)}
        </dl>
        <p><Link to="/contact" className="firm-secondary">{t('Still stuck? Write to us and we’ll help')} &rarr;</Link></p>
      </section>

      <div className="firm-cta">
        <Link to={user ? '/app/accounts/new' : '/signup'} className="cta-button">{user ? t('Connect Interactive Brokers') : t('Start your {n}-day free trial', { n: TRIAL_DAYS })} &rarr;</Link>
      </div>
    </PublicShell>
  );
}
